import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { api } from '@/App';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { useDarkMode } from '@/hooks/useDarkMode';
import useScrollLock from '@/hooks/useScrollLock';
import { relationshipConfig } from '@/components/keepsakes/relationshipConfig';

import { logger } from '@/utils/logger';
import { Gift, Plus, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
export default function RecipientSelectorModal({ product, products, onClose, user, onSuccess, isBulk = false, deckTitle, deckId }) {
  const navigate = useNavigate();
  const darkMode = useDarkMode();
  const [recipients, setRecipients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Keep the browse page from scrolling behind this overlay.
  useScrollLock(true);

  useEffect(() => {
    fetchRecipients();
  }, []);

  const fetchRecipients = async () => {
    try {
      const response = await api.get('/recipients');
      setRecipients(response.data);
    } catch (error) {
      logger.error('Failed to fetch recipients:', error);
      if (!document.querySelector('[data-sonner-toast]')) {
        toast.error('Failed to load recipients');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSelectRecipient = async (recipientId) => {
    setSaving(true);
    try {
      const recipient = recipients.find(r => r.id === recipientId);
      
      if (isBulk && products) {
        // Bulk add all products from deck (bundled together via deck_id)
        const response = await api.post(`/recipients/${recipientId}/saved-gifts/bulk`, {
          product_ids: products.map(p => p.id),
          deck_id: deckId || null,
          deck_name: deckTitle || null
        });

        const { added_count, skipped_count } = response.data;
        
        if (added_count > 0) {
          toast.success(`Added ${added_count} product${added_count > 1 ? 's' : ''} from "${deckTitle}" to ${recipient.name}'s Keepsake!`);
        }
        
        if (skipped_count > 0) {
          toast.info(`${skipped_count} product${skipped_count > 1 ? 's were' : ' was'} already in this keepsake`);
        }
        
        // Dispatch custom event
        window.dispatchEvent(new CustomEvent('keepsakeUpdated', { detail: { recipientId } }));
        if (onSuccess) onSuccess(recipientId);
        
        // Close modal after successful add
        onClose();
      } else {
        // Single product add
        const response = await api.post(`/recipients/${recipientId}/saved-gifts`, {
          recipient_id: recipientId,
          product_id: product.id,
          notes: ''
        });

        if (response.data.already_exists) {
          toast.info(`Already in ${recipient.name}'s Keepsake`);
        } else {
          toast.success(`Saved to ${recipient.name}'s Keepsake!`);
        }
        
        // Dispatch custom event to notify parent components
        window.dispatchEvent(new CustomEvent('keepsakeUpdated', { detail: { recipientId } }));
        
        // Call success callback if provided
        if (onSuccess) {
          onSuccess(recipientId);
        }
        
        // Close modal after successful add
        onClose();
      }
    } catch (error) {
      logger.error('Error saving gift:', error);
      toast.error('Failed to save gift: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  return createPortal(
    <div 
      className="fixed inset-0 flex items-end sm:items-center justify-center"
      style={{
        background: darkMode.isDark ? 'rgba(0, 0, 0, 0.85)' : 'rgba(0, 0, 0, 0.7)',
        backdropFilter: 'blur(4px)',
        zIndex: 99999,
        pointerEvents: 'auto'
      }}
      onClick={onClose}
    >
      <div 
        className="relative w-full sm:max-w-md sm:rounded-2xl rounded-t-2xl shadow-2xl overflow-hidden transition-colors max-h-[85vh] sm:max-h-[80vh] flex flex-col"
        style={{ 
          background: darkMode.isDark ? '#2D4354' : 'white',
          pointerEvents: 'auto'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-6 py-4 border-b transition-colors flex-shrink-0" style={{ borderColor: darkMode.isDark ? '#FED7A5' : '#E5E7EB' }}>
          <button
            onClick={onClose}
            className="absolute top-4 right-4 p-1 rounded-full transition-colors"
            style={{ 
              background: darkMode.isDark ? 'rgba(254, 215, 165, 0.2)' : 'transparent',
              color: darkMode.isDark ? '#FED7A5' : '#73766A'
            }}
            onMouseEnter={(e) => e.currentTarget.style.background = darkMode.isDark ? 'rgba(254, 215, 165, 0.3)' : '#F3F4F6'}
            onMouseLeave={(e) => e.currentTarget.style.background = darkMode.isDark ? 'rgba(254, 215, 165, 0.2)' : 'transparent'}
            data-testid="recipient-selector-close"
          >
            <X className="w-5 h-5" />
          </button>
          <div className="flex items-center gap-3">
            <div
              className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
              style={{ background: 'linear-gradient(135deg, #9E6752 0%, #534145 100%)' }}
            >
              <Gift className="w-5 h-5" style={{ color: '#FED7A5' }} />
            </div>
            <div>
              <h3 className="text-xl font-bold transition-colors" style={{ color: darkMode.textPrimary, fontFamily: 'Playfair Display, serif' }}>
                {isBulk ? 'Add Bundle to a Keepsake' : 'Add to a Keepsake'}
              </h3>
              <p className="text-sm mt-0.5 transition-colors" style={{ color: darkMode.textSecondary }}>
                Who is this for?
              </p>
            </div>
          </div>
        </div>

        {/* Content - Scrollable */}
        <div className="p-6 overflow-y-auto flex-1">
          {loading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#9E6752] mx-auto"></div>
            </div>
          ) : recipients.length === 0 ? (
            <div className="text-center py-8">
              <Gift className="w-12 h-12 mx-auto mb-3" style={{ color: '#9E6752', opacity: 0.5 }} />
              <p className="text-base mb-2 transition-colors" style={{ color: darkMode.textSecondary }}>No keepsakes yet</p>
              <p className="text-sm mb-4 transition-colors" style={{ color: darkMode.textSecondary }}>Create a keepsake for someone to start saving gift ideas</p>
              <Button
                onClick={() => {
                  onClose();
                  navigate('/keepsakes');
                }}
                style={{ background: '#9E6752', color: 'white' }}
              >
                <Plus className="w-4 h-4 mr-2" />
                Create Keepsake
              </Button>
            </div>
          ) : (
            <div className="space-y-3">
              {recipients.map(recipient => {
                const relConfig = relationshipConfig[recipient.relationship] || relationshipConfig['Friend'];
                const RelIcon = relConfig.icon;
                const chipColor = recipient.color || relConfig.color;
                return (
                <button
                  key={recipient.id}
                  onClick={() => handleSelectRecipient(recipient.id)}
                  disabled={saving}
                  className="w-full flex items-center gap-3 p-3 rounded-lg transition-colors text-left"
                  style={{ 
                    border: `2px solid ${darkMode.isDark ? '#FED7A5' : '#E5E7EB'}`,
                    background: darkMode.isDark ? 'rgba(254, 215, 165, 0.05)' : 'transparent'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.background = darkMode.isDark ? 'rgba(254, 215, 165, 0.15)' : '#F9FAFB'}
                  onMouseLeave={(e) => e.currentTarget.style.background = darkMode.isDark ? 'rgba(254, 215, 165, 0.05)' : 'transparent'}
                >
                  <div 
                    className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0"
                    style={{ 
                      background: `linear-gradient(135deg, ${chipColor} 0%, ${chipColor}dd 100%)`
                    }}
                  >
                    <RelIcon className="w-6 h-6 text-white" />
                  </div>
                  <div className="flex-1">
                    <p className="font-semibold transition-colors" style={{ color: darkMode.textPrimary }}>{recipient.name}</p>
                    {recipient.occasion && (
                      <p className="text-xs transition-colors" style={{ color: darkMode.textSecondary }}>{recipient.occasion}</p>
                    )}
                  </div>
                </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer - Fixed at bottom */}
        {recipients.length > 0 && (
          <div className="px-6 py-4 border-t transition-colors flex-shrink-0" style={{ 
            borderColor: darkMode.isDark ? '#FED7A5' : '#E5E7EB', 
            background: darkMode.isDark ? 'rgba(254, 215, 165, 0.05)' : '#F9FAFB' 
          }}>
            <button
              onClick={() => {
                onClose();
                navigate('/keepsakes');
              }}
              className="w-full flex items-center justify-center gap-2 py-2 text-sm hover:underline transition-colors"
              style={{ color: darkMode.isDark ? '#FED7A5' : '#9E6752' }}
            >
              <Plus className="w-4 h-4" />
              Create New Keepsake
            </button>
          </div>
        )}
      </div>
    </div>,
    document.body
  );
}
